import type { TranscriptSelection } from "./transcript";

export function hierarchyUrl(accountId: string, workspaceId?: string, projectId?: string): string {
  const root = `/accounts/${encodeURIComponent(accountId)}/workspaces`;
  if (!workspaceId) return root;
  const workspace = `${root}/${encodeURIComponent(workspaceId)}`;
  return projectId ? `${workspace}/projects/${encodeURIComponent(projectId)}` : workspace;
}

export function transcriptUrl(selection: TranscriptSelection): string {
  if (selection.accountHistory) return accountHistoryUrl(selection.accountId, selection.threadId);
  return `${hierarchyUrl(selection.accountId, selection.workspaceId, selection.projectId)}/threads/${encodeURIComponent(selection.threadId)}`;
}

export function accountHistoryUrl(accountId: string, threadId?: string): string {
  const root = `/accounts/${encodeURIComponent(accountId)}/director-history`;
  return threadId ? `${root}/threads/${encodeURIComponent(threadId)}` : root;
}

// Navigation hints are not authorization. Accept only canonical hierarchy
// routes; destination loaders still authorize every account and resource.
export function hierarchyDestination(value: unknown): { path: string; accountId?: string } | undefined {
  if (value === "/spaces") return { path: value };
  if (typeof value !== "string" || value.length > 2048) return undefined;
  const history = /^\/accounts\/([^/]+)\/director-history(?:\/threads\/([^/]+))?$/.exec(value);
  const match = history ?? /^\/accounts\/([^/]+)\/workspaces(?:\/([^/]+)(?:\/projects\/([^/]+))?(?:\/threads\/([^/]+))?)?$/.exec(value);
  if (!match) return undefined;
  const ids: (string | undefined)[] = [];
  try {
    for (const part of match.slice(1)) {
      const id = part === undefined ? undefined : decodeURIComponent(part);
      if (id !== undefined && (!id || id.length > 512 || id === "." || id === ".." || /[\/\\%?#\s\u0000-\u0020\u007f]/.test(id))) return undefined;
      ids.push(id);
    }
  } catch { return undefined; }
  if (history) return { path: accountHistoryUrl(ids[0]!, ids[1]), accountId: ids[0] };
  const [accountId, workspaceId, projectId, threadId] = ids;
  const path = threadId
    ? transcriptUrl({ accountId: accountId!, workspaceId: workspaceId!, projectId, threadId })
    : hierarchyUrl(accountId!, workspaceId, projectId);
  return { path, accountId };
}
